/**
 * Applies filters to an image using canvas
 * @param {HTMLImageElement} image - The original image element
 * @param {Object} filters - The filter settings to apply
 * @param {HTMLCanvasElement} canvas - The canvas element to use
 * @returns {Promise<string>} - Promise resolving to the filtered image data URL
 */
export const applyFilter = (image, filters, canvas) => {
  return new Promise((resolve, reject) => {
    try {
      // Set canvas dimensions to match image while maintaining aspect ratio
      const maxDimension = 2000; // Prevent very large images from causing performance issues
      let width = image.width;
      let height = image.height;
      
      // Scale down if image is too large
      if (width > maxDimension || height > maxDimension) {
        const aspectRatio = width / height;
        if (width > height) {
          width = maxDimension;
          height = width / aspectRatio;
        } else {
          height = maxDimension;
          width = height * aspectRatio;
        }
      }
      
      // Apply dimensions to canvas
      canvas.width = width;
      canvas.height = height;
      
      // Get canvas context
      const ctx = canvas.getContext('2d');
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Apply filters using CSS filter if possible
      ctx.filter = `
        brightness(${filters.brightness}%)
        contrast(${filters.contrast}%)
        saturate(${filters.saturation}%)
        grayscale(${filters.grayscale}%)
        sepia(${filters.sepia}%)
        blur(${filters.blur}px)
        hue-rotate(${filters.hueRotate}deg)
        invert(${filters.invert}%)
      `;
      
      // Draw image with filters
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      
      // Reset filters
      ctx.filter = 'none';
      
      // Convert to data URL
      const dataUrl = canvas.toDataURL('image/png');
      resolve(dataUrl);
    } catch (error) {
      console.warn('Filter application failed, using fallback method:', error);
      // Fallback for browsers that don't support ctx.filter
      try {
        // Reset canvas
        canvas.width = image.width;
        canvas.height = image.height;
        const ctx = canvas.getContext('2d');
        
        // Draw original image
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        
        // Apply filters manually
        applyManualFilters(ctx, filters, canvas.width, canvas.height);
        
        // Convert to data URL
        const dataUrl = canvas.toDataURL('image/png');
        resolve(dataUrl);
      } catch (fallbackError) {
        reject(fallbackError);
      }
    }
  });
};

/**
 * Manually applies filters when ctx.filter is not supported
 * Note: This is a simplified implementation that doesn't support all filters
 * @param {CanvasRenderingContext2D} ctx - The canvas context
 * @param {Object} filters - The filter settings to apply
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 */
const applyManualFilters = (ctx, filters, width, height) => {
  // Get image data
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  // Brightness and contrast
  const brightnessRatio = filters.brightness / 100;
  const contrastFactor = (259 * (filters.contrast + 255)) / (255 * (259 - filters.contrast));
  
  // Apply grayscale first if needed
  const grayscaleAmount = filters.grayscale / 100;
  const sepiaAmount = filters.sepia / 100;
  const invertAmount = filters.invert / 100;
  
  // Process each pixel
  for (let i = 0; i < data.length; i += 4) {
    let r = data[i];
    let g = data[i + 1];
    let b = data[i + 2];
    
    // Apply grayscale
    if (grayscaleAmount > 0) {
      const gray = (r * 0.3 + g * 0.59 + b * 0.11);
      r = r * (1 - grayscaleAmount) + gray * grayscaleAmount;
      g = g * (1 - grayscaleAmount) + gray * grayscaleAmount;
      b = b * (1 - grayscaleAmount) + gray * grayscaleAmount;
    }
    
    // Apply sepia
    if (sepiaAmount > 0) {
      const sepiaR = (r * 0.393 + g * 0.769 + b * 0.189);
      const sepiaG = (r * 0.349 + g * 0.686 + b * 0.168);
      const sepiaB = (r * 0.272 + g * 0.534 + b * 0.131);
      r = r * (1 - sepiaAmount) + sepiaR * sepiaAmount;
      g = g * (1 - sepiaAmount) + sepiaG * sepiaAmount;
      b = b * (1 - sepiaAmount) + sepiaB * sepiaAmount;
    }
    
    // Apply brightness
    r *= brightnessRatio;
    g *= brightnessRatio;
    b *= brightnessRatio;
    
    // Apply contrast
    r = truncateColor(contrastFactor * (r - 128) + 128);
    g = truncateColor(contrastFactor * (g - 128) + 128);
    b = truncateColor(contrastFactor * (b - 128) + 128);
    
    // Apply invert
    if (invertAmount > 0) {
      r = r * (1 - invertAmount) + (255 - r) * invertAmount;
      g = g * (1 - invertAmount) + (255 - g) * invertAmount;
      b = b * (1 - invertAmount) + (255 - b) * invertAmount;
    }
    
    data[i] = truncateColor(r);
    data[i + 1] = truncateColor(g);
    data[i + 2] = truncateColor(b);
  }
  
  // Put the image data back
  ctx.putImageData(imageData, 0, 0);
};

/**
 * Ensures color values stay between 0-255
 * @param {number} value - The color value to check
 * @returns {number} - The truncated value
 */
const truncateColor = (value) => {
  if (value < 0) return 0;
  if (value > 255) return 255;
  return Math.round(value);
}; 